"use client";
import { AnimatePresence, motion } from "framer-motion";
import { Banner, type BannerProps } from "./Banner";

export interface PageBannerProps extends BannerProps {
  /** Whether the banner is visible */
  isOpen?: boolean;
  /** Position relative to the navbar */
  position?: "fixed" | "sticky";
}

/**
 * PageBanner
 *
 * A banner component designed for page-level announcements, positioned at the top
 * of the page below the navbar. Automatically handles enter/exit animations and
 * can be fixed or sticky positioned.
 *
 * Use this for system-wide messages, announcements, or important alerts that need
 * to be visible across the entire page.
 *
 * @example
 * ```tsx
 * <PageBanner
 *   isOpen={showBanner}
 *   variant="warning"
 *   title="Scheduled maintenance"
 *   dismissible
 *   onDismiss={() => setShowBanner(false)}
 * >
 *   Our systems will be under maintenance on Friday from 2-4 PM EST.
 * </PageBanner>
 * ```
 */
export function PageBanner({ isOpen = true, position = "sticky", className, ...bannerProps }: PageBannerProps) {
  const positionClasses = position === "fixed" ? "fixed top-0 left-0 right-0 z-50" : "sticky top-0 z-50";

  // Remove rounded corners when banner is edge-to-edge (sticky/fixed)
  const bannerClassName = className ? `${className} !rounded-none` : "!rounded-none";

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className={positionClasses}
        >
          <Banner {...bannerProps} className={bannerClassName} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
